import { Alert, Button, HStack, Text } from '@chakra-ui/react';
import { Modal } from '@gamma/ui/overlay';
import { i18n } from '@investigator/shared/localization';
import { useEntitiesContext } from '../../context';
import { useEntityEdrContext } from '../../context/EntityEdrContext';
import { EDRIntegration } from '@investigator/features/integrations/pages/IntegrationsConstants';

const { entityEDR } = i18n.pages.detections;
const { edrAction } = entityEDR;

// No props needed - everything comes from context
export const EntityIsolationModal = (): JSX.Element => {
  // Get UI state from EntitiesContext
  const {
    selectedEntity,
    isolationModal: { isOpen, onClose, isolationType },
  } = useEntitiesContext();

  // Get EDR data and operations from EntityEdrContext
  const { isIsolated, performEdrAction, performEdrActionLoading, edrData } =
    useEntityEdrContext();

  const handleConfirm = async () => {
    if (!isolationType || !selectedEntity?.tenantInfo?.tenant_id) return;

    // Get the entity ID based on which provider
    const entityId =
      isolationType === EDRIntegration.CROWDSTRIKE
        ? edrData?.crowdstrike?.entity_id
        : isolationType === EDRIntegration.MICROSOFT_DEFENDER
          ? edrData?.microsoft_defender?.entity_id
          : undefined;

    if (!entityId) return;

    const action = isIsolated ? 'lift_isolation' : 'isolate';

    try {
      await performEdrAction({
        entityId,
        action,
        tenant: selectedEntity.tenantInfo.tenant_id,
        providerName: isolationType,
      });
      onClose();
    } catch {
      // Error toast shown by mutation onError handler, modal stays open
    }
  };

  const platformName =
    isolationType === EDRIntegration.CROWDSTRIKE
      ? 'Crowdstrike'
      : isolationType === EDRIntegration.MICROSOFT_DEFENDER
        ? 'Microsoft Defender'
        : '';

  const title = `${
    isIsolated ? edrAction.liftEntityIsolation : edrAction.isolateEntity
  } in ${platformName}`;

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      body={
        isIsolated ? (
          <Text>
            <Text as="span" textStyle="body-md-bold">
              {selectedEntity?.entityName}
            </Text>{' '}
            {edrAction.willBeAdded}
          </Text>
        ) : (
          <Alert variant="subtle" status="warning">
            <Text>
              <Text as="span" textStyle="body-md-bold">
                {selectedEntity?.entityName}
              </Text>{' '}
              {edrAction.willBeIsolated}
            </Text>
          </Alert>
        )
      }
      footer={
        <HStack>
          <Button variant="solid" colorScheme="gray" onClick={onClose}>
            {edrAction.cancel}
          </Button>
          <Button
            variant="solid"
            colorScheme="blue"
            onClick={handleConfirm}
            isLoading={performEdrActionLoading}
            isDisabled={performEdrActionLoading}
          >
            {isIsolated ? edrAction.liftIsolation : edrAction.isolateEntity}
          </Button>
        </HStack>
      }
    />
  );
};
